import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import { withTranslation } from 'language/i18n';
import { getObjectByLanguage } from 'language/helpers';
import { PaymentContext } from 'contexts/paymentContext';
import { paymentConsts } from 'constants/payment';
import maskAmount from 'utils/maskAmount';
import { CallTo, MailTo } from 'components/common';
import { turkishNumberWithSuffix } from 'utils/numberFormatters';
import { getDocumentIconClassName } from './ImportantInformations.utils';

const ImportantInformations = ({ t, machine }) => {
  const { paymentData } = useContext(PaymentContext);
  const [documentsVisible, setDocumentsVisible] = useState(true);
  const [paymentDetailsVisible, setPaymentDetailsVisible] = useState(true);
  const [shippingAndDeliveryDetailsVisible, setShippingAndDeliveryDetailsVisible] = useState(true);
  const [financingVisible, setFinancingVisible] = useState(true);
  const [contactUsVisible, setContactUsVisible] = useState(true);
  const [machineResponsibleVisible, setMachineResponsibleVisible] = useState(true);

  return (
    <div className="im-box im-box--white im-box--border-radius im-box--list-item im-box--padding-sm-15">
      <div className="row mb-4">
        <div className="col">
          <h2 className="fs-21 fw-900">{t('importantInformations')}</h2>
        </div>
      </div>

      {/* DOKÜMANLAR */}
      {!isEmpty(machine.additionalDocuments) && (
        <>
          <div className="row">
            <div className="col">
              <div
                className={`im-accordion im-accordion--type2 im-accordion--rotate-icon ${documentsVisible ? 'im-accordion--active' : ''}`}
              >
                <div className="im-accordion__head" onClick={() => setDocumentsVisible((val) => !val)}>
                  <div className="im-accordion__head__title">
                    <h3 className="fs-13 fw-900 text-uppercase">{t('documents')}</h3>
                  </div>
                  <div className="im-accordion__head__toggle-icon d-lg-none">
                    <i className="fa-solid fa-angle-down" />
                  </div>
                </div>
                <div className="im-accordion__body pb-3 pb-lg-0">
                  <div className="row" style={{ rowGap: '1rem' }}>
                    {machine.additionalDocuments.map((doc) => (
                      <div key={doc._id} className="col-15 col-xl" style={{ minWidth: '50%' }}>
                        <a href={doc.s3Uri} target="_blank" rel="noreferrer" className="im-button im-button--less-gutter im-button--fluid im-button--gray-outline-yellow">
                          <div className="im-button__icon im-button__icon--left">
                            <i className={`fal ${getDocumentIconClassName(doc)}`} />
                          </div>
                          <span className="fs-14 fw-500">{getObjectByLanguage(doc.documentType)}</span>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SEPARATOR */}
          <div className="row d-none d-lg-flex">
            <div className="col">
              <div className="separator separator--horizontal separator--neg-margin-15 mt-4 mb-4" />
            </div>
          </div>
        </>
      )}

      {/* ÖDEME DETAYLARI */}
      <div className="row">
        <div className="col">
          <div
            className={`im-accordion im-accordion--type2 im-accordion--rotate-icon ${paymentDetailsVisible ? 'im-accordion--active' : ''}`}
          >
            <div className="im-accordion__head" onClick={() => setPaymentDetailsVisible((val) => !val)}>
              <div className="im-accordion__head__title">
                <h3 className="fs-13 fw-900 text-uppercase">{t('paymentDetails')}</h3>
              </div>
              <div className="im-accordion__head__toggle-icon d-lg-none">
                <i className="fa-solid fa-angle-down" />
              </div>
            </div>
            <div className="im-accordion__body pb-3 pb-lg-0">
              <div className="row">
                <div className="col text-gray3">
                  <p className="fs-13">
                    Kazandığınız açık artırmanın bütün ödemesini 5 iş günü içerisinde ödemeniz gerekmektedir.
                    Ödeme 5 iş gününü geçtiği takdirde gecikme faizi uygulanacaktır.
                  </p>
                  <p className="fs-13">
                    <span className="fw-500 text-black1">
                      Platform Kullanım Ücreti:
                    </span>
                    {' '}
                    Makina satış fiyatının
                    <span className="fw-500 text-black1">
                      {` %${turkishNumberWithSuffix(paymentData.platformUsageFeePercentage)} `}
                    </span>
                    tutarı. (
                    {maskAmount(paymentConsts.PLATFORM_USAGE_FEE_MINIMUM_EUR, undefined, true)}
                    {' '}
                    Euro’dan az olmamak şartıyla)
                    <br />
                    *Açık artırmayı kazanan alıcı, platform kullanım ücretini ödemek zorundadır.
                  </p>
                  <p className="fs-13">
                    Ödemelerinizi Havale veya EFT yoluyla gerçekleştirebilirsiniz.
                  </p>
                  <p className="fs-13 mb-0">
                    Açık artırma teklifleri alıcının sorumluluğundadır.
                    Yapılan teklifler geri alınamaz. Alıcı, teklifini iptal edilmek isterse, teminat bedeli iade hakkını kaybedecektir.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEPARATOR */}
      <div className="row d-none d-lg-flex">
        <div className="col">
          <div className="separator separator--horizontal separator--neg-margin-15 mt-4 mb-4" />
        </div>
      </div>

      {/* NAKLİYE VE TESLİM DETAYLARI */}
      <div className="row">
        <div className="col">
          <div
            className={`im-accordion im-accordion--type2 im-accordion--rotate-icon ${shippingAndDeliveryDetailsVisible ? 'im-accordion--active' : ''}`}
          >
            <div className="im-accordion__head" onClick={() => setShippingAndDeliveryDetailsVisible((val) => !val)}>
              <div className="im-accordion__head__title">
                <h3 className="fs-13 fw-900 text-uppercase">{t('shippingAndDeliveryDetails')}</h3>
              </div>
              <div className="im-accordion__head__toggle-icon d-lg-none">
                <i className="fa-solid fa-angle-down" />
              </div>
            </div>
            <div className="im-accordion__body pb-3 pb-lg-0">
              <div className="row">
                <div className="col text-gray3">
                  <p className="fs-13">
                    Makinanın nakliyesinden Alıcı sorumludur.
                    iyiMakina.com ve Satıcı'nın nakliye ile ilgili sorumluluğu yoktur.
                  </p>
                  <p className="fs-13">
                    Ödemesinin tamamı yapılmış makinanın 8 gün içerisinde nakledilmesi gerekmektedir.
                    Geciken nakliye süresi için gecikme bedeli tahsil edilecektir.
                  </p>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col">
                  <h3 className="fs-13 fw-900">Teslimat Koşulları</h3>
                </div>
              </div>
              <div className="row">
                <div className="col text-gray3">
                  <p className="fs-13">
                    Teslimat şartlarına uymayan alıcı, olası yaptırımları kabul etmektedir.
                  </p>
                  <p className="fs-13">
                    Alıcı, açık artırmayı kazanıp, toplam ödemeyi yaptıktan 24 saat sonra, nakliyenin tarih ve süresini satıcıya bildirmek durumundadır.
                  </p>
                  <p className="fs-13">
                    Satıştaki makina Lowbed tipi nakliyeye uygundur.
                  </p>
                  <p className="fs-13 mb-0">
                    Makinanın teslimat koşullarını öğrenmek için iyiMakina Çağrı Merkezi'ni arayabilir veya e-posta yoluyla iletişime geçebilirsiniz.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEPARATOR */}
      <div className="row d-none d-lg-flex">
        <div className="col">
          <div className="separator separator--horizontal separator--neg-margin-15 mt-4 mb-4" />
        </div>
      </div>

      {/* FİNANSMAN */}
      <div className="row">
        <div className="col">
          <div
            className={`im-accordion im-accordion--type2 im-accordion--rotate-icon ${financingVisible ? 'im-accordion--active' : ''}`}
          >
            <div className="im-accordion__head" onClick={() => setFinancingVisible((val) => !val)}>
              <div className="im-accordion__head__title">
                <h3 className="fs-13 fw-900 text-uppercase">{t('financing')}</h3>
              </div>
              <div className="im-accordion__head__toggle-icon d-lg-none">
                <i className="fa-solid fa-angle-down" />
              </div>
            </div>
            <div className="im-accordion__body pb-3 pb-lg-0">
              <div className="row mb-2">
                <div className="col">
                  <h3 className="fs-13 fw-900">Güvenebileceğiniz Çözümler</h3>
                </div>
              </div>
              <div className="row">
                <div className="col text-gray3">
                  <p className="fs-13 mb-0">
                    Her türlü finansman desteği için bize ulaşabilir, iyiMakina.com finansal hizmetlerimizden yararlanabilirsiniz.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEPARATOR */}
      <div className="row d-none d-lg-flex">
        <div className="col">
          <div className="separator separator--horizontal separator--neg-margin-15 mt-4 mb-4" />
        </div>
      </div>

      {/* BİZE ULAŞIN */}
      <div className="row">
        <div className="col">

          <div
            className={`im-accordion im-accordion--type2 im-accordion--rotate-icon ${contactUsVisible ? 'im-accordion--active' : ''}`}
          >
            <div className="im-accordion__head" onClick={() => setContactUsVisible((val) => !val)}>
              <div className="im-accordion__head__title">
                <h3 className="fs-13 fw-900 text-uppercase">{t('contactUs')}</h3>
              </div>
              <div className="im-accordion__head__toggle-icon d-lg-none">
                <i className="fa-solid fa-angle-down" />
              </div>
            </div>
            <div className="im-accordion__body pb-3 pb-lg-0" style={{ lineHeight: 1 }}>
              <p className="fs-13">
                <span className="fw-500 text-black1">
                  {`${t('companyTitle')}: `}
                  {' '}
                </span>
                iyiMakina Bilgi Teknolojileri A.Ş
              </p>
              <p className="fs-13" style={{ lineHeight: 1 }}>
                <span className="fw-500 text-black1">
                  {`${t('commercialRegistrationNumber')}: `}
                  {' '}
                </span>
                3434934
              </p>
              <p className="fs-13" style={{ lineHeight: 1 }}>
                <span className="fw-500 text-black1">
                  {`${t('address')}: `}
                  {' '}
                </span>
                İTU TEKNOKENT ARI 4  Sarıyer/İstanbul
              </p>
              <p className="fs-13" style={{ lineHeight: 1 }}>
                <span className="fw-500 text-black1">
                  {`${t('phoneNumber')}: `}
                  {' '}
                </span>
                <CallTo style={{ color: '#848689', fontWeight: '400' }} tel="+90 (850) 885 46 25" />
              </p>
              <p className="fs-13" style={{ lineHeight: 1 }}>
                <span className="fw-500 text-black1">
                  Fax:
                  {' '}
                </span>
                <CallTo style={{ color: '#848689', fontWeight: '400' }} tel="+90 (850) 885 46 25" />
              </p>
              <p className="fs-13 mb-0" style={{ lineHeight: 1 }}>
                <span className="fw-500 text-black1">
                  {`${t('email')}: `}
                  {' '}
                </span>
                <MailTo style={{ color: '#848689', fontWeight: '400' }} mail="info@iyimakina.com" />
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SEPARATOR */}
      <div className="row d-none d-lg-flex">
        <div className="col">
          <div className="separator separator--horizontal separator--neg-margin-15 mt-4 mb-4" />
        </div>
      </div>

      {/* MAKINA SORUMLUSU */}
      <div className="row">
        <div className="col">
          <div
            className={`im-accordion im-accordion--type2 im-accordion--rotate-icon ${machineResponsibleVisible ? 'im-accordion--active' : ''}`}
          >
            <div className="im-accordion__head" onClick={() => setMachineResponsibleVisible((val) => !val)}>
              <div className="im-accordion__head__title">
                <h3 className="fs-13 fw-900 text-uppercase">{t('MachineResponsible')}</h3>
              </div>
              <div className="im-accordion__head__toggle-icon d-lg-none">
                <i className="fa-solid fa-angle-down" />
              </div>
            </div>
            <div className="im-accordion__body pb-3 pb-lg-0">
              <div className="row">
                <div className="col text-gray3">
                  <p className="fs-13 mb-0">{t('MachineResponsibleExplanation')}</p>
                </div>
              </div>
              {/* <div className="machine-owner-container">
                <div className="machine-owner-info-container">
                  <img
                    src="/image/favicon.ico"
                    className="machine-owner-photo"
                    alt="owner_photo"
                  />
                  <div className="machine-owner-info">
                    <p className="owner-fullname">{machine.owner.fullname}</p>
                    <p className="owner-membership-type">{machine.owner.membershipType.tr}</p>
                  </div>
                </div>
                <div className="machine-owner-contact-main">
                  <div className="machine-owner-contact-container">
                    <i className="far fa-phone-alt fs-18" />
                    <p className="owner-contact">{machine.owner.membershipType.tr}</p>
                  </div>
                  <div className="machine-owner-contact-container">
                    <i className="far fa-envelope fs-20" />
                    <p className="owner-contact">{machine.owner.mail}</p>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ImportantInformations.propTypes = {
  t: PropTypes.func,
  machine: PropTypes.object,
};

export default withTranslation('common')(ImportantInformations);
